import { z } from 'zod';
import { type PermissionCode } from '../../rbac/index.js';
import { REFUSAL_COMMENT_MAX_LENGTH, RequestRefusalReasonSchema, RequestStatus } from './status.js';

/**
 * @aiPurpose static request state-machine — every legal status transition
 *   plus the permission code(s) that gate it. Enforcement lives in the
 *   backend transition service.
 * @aiInvariant gating moved from the legacy `roles: Role[]` literal
 *   ('manager','logist_cn','admin') to `permissions: PermissionCode[]` as
 *   part of #92. `'requests.transition.<key>'` codes are owned by this
 *   matrix and never appear elsewhere in the catalog.
 * @aiSecurity gating decisions for high-risk request transitions
 *   (process / close / archive) flow through this matrix; do not bypass.
 */
export const REQUEST_TRANSITIONS = {
  // WHY (#961): `send` now stages the request in `READY_FOR_WORK` instead
  //   of jumping straight to `IN_WORK`. The first carrier addition is the
  //   atomic IN_WORK transition (emitted as `TRANSITION_AUTO_START` from
  //   `RequestCarriersService.addCarrier`). The SLA clock still starts
  //   here — `inWorkAt` + `slaDeadline` are stamped on send so the
  //   countdown begins before a logist has attached the first carrier.
  send: {
    from: [RequestStatus.DRAFT, RequestStatus.RETURN],
    to: RequestStatus.READY_FOR_WORK,
    permissions: ['requests.transition.send'] as readonly PermissionCode[],
    requiresItems: true,
  },
  // WHY (#961, widened by #2099/#2135): the four active pre-terminal
  //   states are legal return sources. Per-actor authority is layered in
  //   the orchestrator party gate (BR-3): a MANAGER may return from any of
  //   the four (IN_WORK / PROCESSED / POSTPONED and the pre-pickup
  //   READY_FOR_WORK); a LOGIST only from {READY_FOR_WORK, IN_WORK}. The
  //   matrix lists the union so the cross-status `def.from` check stays
  //   correct for both actor sides — the per-actor narrowing lives at the
  //   orchestrator. Returns from a carrier-bearing source also freeze a
  //   `RequestReturnSnapshot` and clear live carriers/bids (#2135).
  return: {
    from: [
      RequestStatus.READY_FOR_WORK,
      RequestStatus.IN_WORK,
      RequestStatus.PROCESSED,
      RequestStatus.POSTPONED,
    ],
    to: RequestStatus.RETURN,
    permissions: ['requests.transition.return'] as readonly PermissionCode[],
    requireReason: true,
  },
  process: {
    from: [RequestStatus.IN_WORK],
    to: RequestStatus.PROCESSED,
    permissions: ['requests.transition.process'] as readonly PermissionCode[],
    requiresAnsweredCarrier: true,
  },
  // WHY (#1059): logist_cn-side reopen of a PROCESSED row back to
  //   IN_WORK when more logistics work is needed. Authority is the
  //   assigned logist's alone — orchestrator narrows side='logist' AND
  //   requires `requests.transition.reopen`. Reason is required for
  //   audit (stored in the audit row's `reason` column, NOT in
  //   `returnReason` since the destination status is IN_WORK, not
  //   RETURN). Service-layer mutates clear `processedAt` +
  //   `winnerBidId`, restart SLA (`inWorkAt = now`, `slaDeadline = now +
  //   SLA_HOURS`), and stamp reopen-cycle metadata (`reopenedAt`,
  //   `reopenedCount`). Existing carriers, bids, chat, items, boxes,
  //   and comments are preserved.
  reopen: {
    from: [RequestStatus.PROCESSED],
    to: RequestStatus.IN_WORK,
    permissions: ['requests.transition.reopen'] as readonly PermissionCode[],
    requireReason: true,
  },
  // WHY (#2133, BR-1): the China Logist declares a request un-transportable
  //   («Не можем везти») from IN_WORK. Terminal `our-fault` outcome distinct
  //   from a customer REFUSED; requires a reason (stored on the audit row).
  //   Foundation only — no controller route or web action surfaces this
  //   transition yet (the callable endpoint lands in #2136, the web action in
  //   #2139); the per-role authority narrowing (logist_cn only) is added by
  //   the orchestrator in #2135.
  cannotTransport: {
    from: [RequestStatus.IN_WORK],
    to: RequestStatus.CANNOT_TRANSPORT,
    permissions: ['requests.transition.cannotTransport'] as readonly PermissionCode[],
    requireReason: true,
  },
  // WHY (#585): legacy single close transition. Retained for backend
  //   compatibility and existing test coverage; the normal UI flow uses
  //   success/refuse instead. Reach removal is out of scope.
  close: {
    from: [RequestStatus.PROCESSED],
    to: RequestStatus.CLOSED,
    permissions: ['requests.transition.close'] as readonly PermissionCode[],
    requiresWinner: true,
  },
  // WHY (#585): manager-side finalisation when the customer accepts the
  //   logistics quote. Requires an explicit winning bid id in the request
  //   body; the service re-validates membership + carrier-state ANSWERED
  //   at COMMIT (same defense-in-depth as `close`).
  // WHY (#1836): source statuses widened to revive a parked/refused row.
  //   From REFUSED (winner null) or POSTPONED-without-winner the manager
  //   re-picks the winner first (the success modal commits pickWinner +
  //   success as a pair); leaving REFUSED also clears the refusal fields
  //   at commit. `requiresWinner` stays authoritative at commit for every
  //   source status.
  success: {
    from: [RequestStatus.PROCESSED, RequestStatus.REFUSED, RequestStatus.POSTPONED],
    to: RequestStatus.SUCCESS,
    permissions: ['requests.transition.success'] as readonly PermissionCode[],
    requiresWinner: true,
  },
  // WHY (#585): manager-side finalisation when the customer refuses.
  //   Requires a structured `RequestRefusalReason`; the optional
  //   comment is trimmed + bounded at the wire layer and stored on the
  //   row only (never duplicated into audit ledger payload).
  // WHY (#1836): POSTPONED added as a legal source so a parked request can
  //   be refused directly; behaviour is identical to PROCESSED -> REFUSED.
  refuse: {
    from: [RequestStatus.PROCESSED, RequestStatus.POSTPONED],
    to: RequestStatus.REFUSED,
    permissions: ['requests.transition.refuse'] as readonly PermissionCode[],
    requireRefusalReason: true,
  },
  // WHY (#1836): manager-side parking of a processed quote when the
  //   customer's decision is pending. POSTPONED is active/non-terminal —
  //   `closedAt` stays null and any stored `winnerBidId` is preserved, so
  //   the request behaves like PROCESSED while parked. Revive via `success`
  //   (re-pick if needed) or `refuse`.
  postpone: {
    from: [RequestStatus.PROCESSED],
    to: RequestStatus.POSTPONED,
    permissions: ['requests.transition.postpone'] as readonly PermissionCode[],
  },
  // WHY (#585): archive now accepts every terminal status (CLOSED,
  //   SUCCESS, REFUSED). The service stores the pre-archive status in
  //   `Request.archivedFromStatus` so unarchive can restore it.
  // WHY (#2133, BR-6): CANNOT_TRANSPORT is terminal and must be archivable
  //   (the «Архив» list shows it like REFUSED). Per-role archive authority
  //   for a CANNOT_TRANSPORT row is narrowed later (#2136); the matrix only
  //   lists it as a legal source here.
  archive: {
    from: [
      RequestStatus.CLOSED,
      RequestStatus.SUCCESS,
      RequestStatus.REFUSED,
      RequestStatus.CANNOT_TRANSPORT,
    ],
    to: RequestStatus.ARCHIVE,
    permissions: ['requests.transition.archive'] as readonly PermissionCode[],
  },
  // WHY (#585): `to: CLOSED` is the matrix-level FROM-fence and the
  //   legacy fallback target. The service-level `unarchive` method
  //   restores `archivedFromStatus` when present and falls back to
  //   CLOSED only for legacy null-archive rows.
  unarchive: {
    from: [RequestStatus.ARCHIVE],
    to: RequestStatus.CLOSED,
    permissions: ['requests.transition.unarchive'] as readonly PermissionCode[],
  },
} as const;

/**
 * @aiPurpose union of every transition name in the state-machine.
 * @aiInvariant equals the keys of `REQUEST_TRANSITIONS` at the type level.
 */
export type TransitionKey = keyof typeof REQUEST_TRANSITIONS;

/**
 * @aiPurpose request body for `POST /requests/:id/transitions/success`.
 *   The canonical winner is `Request.winnerBidId`. As of #1164 the
 *   manager picks the winner via `pickWinner` in IN_WORK OR PROCESSED;
 *   the `process` transition no longer gates on a stored winner. The
 *   body `bidId` is optional and back-compat only — when present it
 *   must match the stored `winnerBidId` or the request is rejected
 *   with the typed `WINNER_REQUIRED` gate code. The success modal
 *   commits `pickWinner` + `success` as a pair so the request always
 *   reaches this endpoint with a stored winner.
 * @aiSecurity gates HIGH-RISK manager-side finalisation; require both
 *   `requests.transition.success` AND the manager-ownership party gate.
 */
export const SuccessTransitionBodySchema = z
  .object({
    bidId: z.string().uuid().optional(),
  })
  .strict();
export type SuccessTransitionBody = z.infer<typeof SuccessTransitionBodySchema>;

/**
 * @aiPurpose request body for `POST /requests/:id/transitions/refuse`.
 *   `reason` is a stable machine code (`RequestRefusalReason`). The
 *   optional `comment` is trimmed at the service layer; empty-after-trim
 *   normalises to null. The raw comment lives ONLY on
 *   `Request.refusalComment` and is never duplicated into audit ledger
 *   payload (a `hasRefusalComment` boolean is what may surface there).
 * @aiSecurity gates HIGH-RISK manager-side finalisation; require both
 *   `requests.transition.refuse` AND the manager-ownership party gate.
 * @aiPii `comment` is free text and may contain PII — bounded and
 *   redacted in logs by the recordMutation pipeline.
 */
export const RefuseTransitionBodySchema = z
  .object({
    reason: RequestRefusalReasonSchema,
    // INVARIANT (#585): comment is bounded at the wire layer; the
    // service trims and normalises empty-after-trim → null. We do NOT
    // call `.trim()` here because trim() on the schema would silently
    // canonicalise the wire payload across all consumers, including
    // tests that assert raw wire shape — the service-level normalise
    // matches the precedent set by other free-text fields in this file.
    comment: z.string().max(REFUSAL_COMMENT_MAX_LENGTH).optional(),
  })
  .strict();
export type RefuseTransitionBody = z.infer<typeof RefuseTransitionBodySchema>;
